import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { CellWrapper, CellContent, CellWithoutDataContent, EllipsisText } from './../styles';
import Input from '../StyledMaskedInput';

const TextInput = ({
  value: propValue = '',
  editable: propEditable = false,
  type,
  onChange,
  left,
  isMissing = false,
  isActive = false,
  isEditing = false,
  onStartEditing,
  onStopEditing,
  rowIndex,
  colIndex,
  registerCellRef,
}) => {
  const [value, setValue] = useState(propValue);
  const [initialValue, setInitialValue] = useState(propValue);
  const [editable, setEditable] = useState(propEditable || isEditing);
  const inputRef = useRef(null);
  const cellRef = useRef(null);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (registerCellRef && typeof rowIndex === 'number' && typeof colIndex === 'number') {
      registerCellRef(rowIndex, colIndex, cellRef.current);

      return () => registerCellRef(rowIndex, colIndex, null);
    }

    return undefined;
  }, [registerCellRef, rowIndex, colIndex]);

  const isCurrentlyEditing = isEditing !== undefined ? isEditing : editable;

  useEffect(() => {
    if (propValue !== initialValue && !propEditable && isEditing === undefined) {
      setValue(propValue);
      setInitialValue(propValue);
      setEditable(false);
    } else if (propValue !== value && isEditing === undefined) {
      setValue(propValue);
      setInitialValue(propValue);
    }
  }, [propValue, propEditable, initialValue, isEditing, value]);

  useEffect(() => {
    if (isEditing !== undefined) {
      setEditable(isEditing);
      if (isEditing && inputRef.current) {
        setTimeout(() => {
          const inputElement = inputRef.current;
          if (inputElement && inputElement.focus && typeof inputElement.focus === 'function') {
            inputElement.focus();
            if (inputElement.select && typeof inputElement.select === 'function') {
              inputElement.select();
            }
          }
        }, 0);
      }
    }
  }, [isEditing]);

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const handleBlur = (e) => {
    if (isSavingRef.current) {
      return;
    }

    const newValue = e.target.value;
    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }

    if (onChange && newValue !== initialValue) {
      onChange(newValue);
    }
  };

  const handleEnterSave = () => {
    isSavingRef.current = true;

    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }

    if (onChange) {
      onChange(value);
    }

    setTimeout(() => {
      isSavingRef.current = false;
    }, 300);
  };

  const handleEscapeCancel = () => {
    isSavingRef.current = false;
    setValue(initialValue);
    if (isEditing !== undefined && onStopEditing) {
      onStopEditing();
    } else {
      setEditable(false);
    }
  };

  const handleEnterEdit = () => {
    if (!isCurrentlyEditing) {
      if (isEditing !== undefined && onStartEditing) {
        onStartEditing();
      } else {
        setEditable(true);
      }
    }
  };

  const edit = () => {
    if (isEditing !== undefined && onStartEditing) {
      onStartEditing();
    } else {
      setEditable(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isCurrentlyEditing) {
        handleEnterSave();
      } else {
        handleEnterEdit();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleEscapeCancel();
    }
  };

  return (
    <CellWrapper
      ref={cellRef}
      isActive={isActive}
      isEditing={isCurrentlyEditing}
      onKeyDown={!isCurrentlyEditing ? handleKeyDown : undefined}
      tabIndex={!isCurrentlyEditing ? 0 : -1}
    >
      {isCurrentlyEditing ? (
        <CellContent>
          <Input
            ref={inputRef}
            value={value}
            onChange={handleChange}
            type={type}
            autoFocus
            onPressEnter={handleEnterSave}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            left={left}
          />
        </CellContent>
      ) : (
        <>
          {value ? (
            <EllipsisText
              left={left}
              onClick={edit}
              isActive={isActive}
            >
              {value}
            </EllipsisText>
          ) : (
            <CellWithoutDataContent
              left={left}
              onClick={edit}
              isMissing={isMissing}
              isActive={isActive}
            />
          )}
        </>
      )}
    </CellWrapper>
  );
};

TextInput.propTypes = {
  value: PropTypes.string,
  editable: PropTypes.bool,
  type: PropTypes.string,
  onChange: PropTypes.func,
  left: PropTypes.bool,
  isMissing: PropTypes.bool,
  isActive: PropTypes.bool,
  isEditing: PropTypes.bool,
  onStartEditing: PropTypes.func,
  onStopEditing: PropTypes.func,
  rowIndex: PropTypes.number,
  colIndex: PropTypes.number,
  registerCellRef: PropTypes.func,
};

TextInput.defaultProps = {
  value: '',
  editable: false,
  type: 'text',
  onChange: null,
  left: false,
  isMissing: false,
  isActive: false,
  isEditing: undefined,
  onStartEditing: null,
  onStopEditing: null,
  rowIndex: undefined,
  colIndex: undefined,
  registerCellRef: null,
};

export default TextInput;
